import {
    AboutDriver,
    AboutVehicle,
    AboutYou,
} from '~/lib/jotai/bb/collision-no-other-vehicle'
import { AboutYou as AboutYouJM } from '~/lib/jotai/jm/collision-no-other-vehicle'
import { LookUpType, Make, Model } from '~/types'
import { formattedMake, formattedModel } from './vehicle'
import { useEffect } from 'react'
import { LocalStorageKey } from './literals'
import { atom, useAtom } from 'jotai'
import { DriversLicenseTypes } from '~/models/dropdowns'
import { Regions } from '~/models/region'
import { regionAtom } from '~/lib/jotai/region'

export interface User {
    IsMainDriver: boolean
    IsMainInsured: boolean
    IsRoadside?: boolean
    PolicyNumber: string
    FirstName: string
    LastName: string
    PhoneNumber: string
    PhoneNumberType: string
    IdentificationNumber: string
    IdentificationNumberType: string
    Email: string
    EmailType: string
    ContactPreference: string
    Location: UserLocation
    Driver: UserDriver
    Vehicle: UserVehicle
    TRN?: string
    // Additional?: UserAdditional
}

export interface UserLocation {
    isMainLocation: boolean
    isMailingAddress: boolean
    Street: string
    StreetNumber: string
    Town: string
    State: string
    Country: string
    District: string
    AddressLine: string
}

export interface UserDriver {
    DateOfBirth: Date | undefined
    LicenseStatus: string
    LicenseType: string
    LicenseNumber: string
    LicenseFirstIssuedDate: Date | undefined
    LicenseCurrentIssuedDate: Date | undefined
    LicenseExpiryDate: Date | undefined
    LicenseCountryIssued: string
}

export interface UserVehicle {
    Year: number | undefined
    Make?: UserVehicleMake
    Model?: UserVehicleModel
    RegistrationNumber: string
    ChassisNumber: string
    EngineNumber: string
    HasVehicleMortgagee: boolean
}

export type UserVehicleMake = Make & { Id: number }
export type UserVehicleModel = Model & { Id: number }

export const emptyUser: User = {
    IsMainDriver: false,
    IsMainInsured: false,
    PolicyNumber: '',
    FirstName: '',
    LastName: '',
    PhoneNumber: '',
    PhoneNumberType: '',
    IdentificationNumber: '',
    IdentificationNumberType: '',
    Email: '',
    EmailType: '',
    ContactPreference: '',
    Location: {
        isMainLocation: false,
        isMailingAddress: false,
        Street: '',
        StreetNumber: '',
        Town: '',
        State: '',
        Country: '',
        District: '',
        AddressLine: '',
    },
    Driver: {
        DateOfBirth: undefined,
        LicenseStatus: '',
        LicenseType: '',
        LicenseNumber: '',
        LicenseFirstIssuedDate: undefined,
        LicenseCurrentIssuedDate: undefined,
        LicenseExpiryDate: undefined,
        LicenseCountryIssued: '',
    },
    Vehicle: {
        Year: undefined,
        Make: undefined,
        Model: undefined,
        RegistrationNumber: '',
        ChassisNumber: '',
        EngineNumber: '',
        HasVehicleMortgagee: false,
    },
}

function RegionState() {
    const [region, setRegion] = useAtom(regionAtom)
    return { region, setRegion }
}

export function GetUserAtom(): User {
    const [user, setUser] = useAtom(userAtom)

    useEffect(() => {
        const val = localStorage.getItem(LocalStorageKey.U)
        const u = val ? JSON.parse(val) : emptyUser

        setUser(u)
    }, [])

    return user
}

export const userAtom = atom<User>(emptyUser)

export function getAboutYouDefault(
    aboutYouData: Partial<AboutYou> | Partial<AboutYouJM>,
    user: User
): Partial<AboutYou> | Partial<AboutYouJM> {
    function mapPhoneType(inputType: string) {
        if (!inputType) return undefined

        const val = localStorage.getItem(LocalStorageKey.StaticLookup)
        const dropdownValues = val ? JSON.parse(val).phoneTypes : undefined

        if (!dropdownValues) return undefined

        const match = dropdownValues.find(
            (option: LookUpType) =>
                (inputType.toLowerCase().includes('mob') &&
                    option.code.toLowerCase().includes('mobile')) ||
                (inputType.toLowerCase().includes('work') &&
                    option.code.toLowerCase().includes('work')) ||
                (inputType.toLowerCase().includes('home') &&
                    option.code.toLowerCase().includes('home'))
        )

        return match ? match.code : undefined
    }

    const region = RegionState().region
    const defaultDetails =
        region === Regions.Barbados.code
            ? {
                  ...aboutYouData,
                  PolicyNumber: aboutYouData.PolicyNumber ?? user.PolicyNumber,
                  PolicyholderFirstName:
                      aboutYouData.PolicyholderFirstName ?? user.FirstName,
                  PolicyholderLastName:
                      aboutYouData.PolicyholderLastName ?? user.LastName,
                  partyReportingIncident:
                      aboutYouData.partyReportingIncident ??
                      (user.IsMainInsured
                          ? 'policyholder'
                          : user?.IsRoadside
                            ? 'roadside assistance'
                            : undefined),
                  ReporterPhoneType:
                      aboutYouData.ReporterPhoneType ??
                      mapPhoneType(user.PhoneNumberType),
                  reportingPhoneNumber:
                      aboutYouData.reportingPhoneNumber ?? user.PhoneNumber,
                  ReporterEmailType:
                      aboutYouData.ReporterEmailType ?? user.EmailType,
                  reportingEmailAddress:
                      aboutYouData.reportingEmailAddress ?? user.Email,
                  ReporterAddressLine1:
                      aboutYouData.ReporterAddressLine1 ??
                      user.Location.AddressLine,
                  ReporterCountry:
                      aboutYouData.ReporterCountry ?? user.Location.Country,
                  ReporterParish:
                      aboutYouData.ReporterParish ?? user.Location.State,
                  ReporterDistrict:
                      aboutYouData.ReporterDistrict ?? user.Location.District,
                  ReporterIdentificationNumber:
                      aboutYouData.ReporterIdentificationNumber ??
                      user.IdentificationNumber,
                  ReporterIdentificationType:
                      aboutYouData.ReporterIdentificationType ??
                      user.IdentificationNumberType,
                  //AreYouDriver: aboutYouData.AreYouDriver ?? (user?.IsRoadside ? "no" : undefined),
                  ReporterAddressType:
                      (aboutYouData.ReporterAddressType ??
                      user.Location.isMainLocation)
                          ? 'contact'
                          : user.Location.isMailingAddress
                            ? 'mailing'
                            : undefined,
                  ReporterContactPreference:
                      aboutYouData.ReporterContactPreference ??
                      user.ContactPreference?.toLowerCase(),
              }
            : {
                  ...aboutYouData,
                  PolicyNumber: aboutYouData.PolicyNumber ?? user.PolicyNumber,
                  PolicyholderFirstName:
                      aboutYouData.PolicyholderFirstName ?? user.FirstName,
                  PolicyholderLastName:
                      aboutYouData.PolicyholderLastName ?? user.LastName,
                  TRN: (aboutYouData as AboutYouJM).TRN ?? user.TRN,
                  partyReportingIncident:
                      aboutYouData.partyReportingIncident ??
                      (user.IsMainInsured
                          ? 'policyholder'
                          : user?.IsRoadside
                            ? 'roadside assistance'
                            : undefined),
                  ReporterPhoneType:
                      aboutYouData.ReporterPhoneType ??
                      mapPhoneType(user.PhoneNumberType),
                  reportingPhoneNumber:
                      aboutYouData.reportingPhoneNumber ?? user.PhoneNumber,
                  ReporterEmailType:
                      aboutYouData.ReporterEmailType ?? user.EmailType,
                  reportingEmailAddress:
                      aboutYouData.reportingEmailAddress ?? user.Email,
                  ReporterAddressLine1:
                      aboutYouData.ReporterAddressLine1 ??
                      user.Location.AddressLine,
                  ReporterCountry:
                      aboutYouData.ReporterCountry ?? user.Location.Country,
                  ReporterParish:
                      aboutYouData.ReporterParish ?? user.Location.State,
                  ReporterDistrict:
                      aboutYouData.ReporterDistrict ?? user.Location.District,
                  ReporterIdentificationNumber:
                      aboutYouData.ReporterIdentificationNumber ??
                      user.IdentificationNumber,
                  ReporterIdentificationType:
                      aboutYouData.ReporterIdentificationType ??
                      user.IdentificationNumberType,
                  //AreYouDriver: aboutYouData.AreYouDriver ?? (user?.IsRoadside ? "no" : undefined),
                  ReporterAddressType:
                      (aboutYouData.ReporterAddressType ??
                      user.Location.isMainLocation)
                          ? 'contact'
                          : user.Location.isMailingAddress
                            ? 'mailing'
                            : undefined,
                  ReporterContactPreference:
                      aboutYouData.ReporterContactPreference ??
                      user.ContactPreference?.toLowerCase(),
              }

    return defaultDetails
}

export function getAboutDriverDefault(
    aboutDriverData: Partial<AboutDriver>,
    driver: UserDriver
): Partial<AboutDriver> {
    function mapLicenseType(inputType: string) {
        if (!inputType) return undefined

        const dropdownValues = DriversLicenseTypes

        const match = dropdownValues.find(
            (option: { name: string; value: string }) =>
                (inputType.toLowerCase().includes('permanent') &&
                    option.value.toLowerCase().includes('permanent')) ||
                (inputType.toLowerCase().includes('learner') &&
                    option.name.toLowerCase().includes('learner')) ||
                (inputType.toLowerCase().includes('visitor') &&
                    option.name.toLowerCase().includes('visitor'))
        )

        return match ? match.value : undefined
    }

    return {
        ...aboutDriverData,
        DriverDateOfBirth:
            aboutDriverData.DriverDateOfBirth ?? driver.DateOfBirth,
        LicenseStatus: aboutDriverData.LicenseStatus ?? driver.LicenseStatus,
        DriverLicenseType:
            aboutDriverData.DriverLicenseType ??
            mapLicenseType(driver.LicenseType),
        LicenseNumber: aboutDriverData.LicenseNumber ?? driver.LicenseNumber,
        DateFirstLicensed:
            aboutDriverData.DateFirstLicensed ?? driver.LicenseFirstIssuedDate,
        CurrentLicenseIssueDate:
            aboutDriverData.CurrentLicenseIssueDate ??
            driver.LicenseCurrentIssuedDate,
        LicenseExpirationDate:
            aboutDriverData.LicenseExpirationDate ?? driver.LicenseExpiryDate,
        LicenseCountry:
            aboutDriverData.LicenseCountry ?? driver.LicenseCountryIssued,
    }
}

export function getAboutVehicleDefault(
    aboutVehicleData: Partial<AboutVehicle>,
    vehicle: UserVehicle
): Partial<AboutVehicle> {
    let altMake = undefined
    let altModel = undefined

    if (vehicle.Make)
        altMake = formattedMake({
            ...vehicle.Make!,
            id: vehicle.Make!.Id,
        }).value
    if (vehicle.Model)
        altModel = formattedModel({
            ...vehicle.Model!,
            id: vehicle.Model!.Id,
        }).value

    return {
        ...aboutVehicleData,
        VehicleYear: aboutVehicleData.VehicleYear ?? vehicle.Year,
        VehicleMake: aboutVehicleData.VehicleMake ?? altMake,
        VehicleModel: aboutVehicleData.VehicleModel ?? altModel,
        VehicleRegistrationNumber:
            aboutVehicleData.VehicleRegistrationNumber ??
            vehicle.RegistrationNumber,
        VehicleChassisNumber:
            aboutVehicleData.VehicleChassisNumber ?? vehicle.ChassisNumber,
        VehicleEngineNumber:
            aboutVehicleData.VehicleEngineNumber ?? vehicle.EngineNumber,
        VehicleMortgagee:
            aboutVehicleData.VehicleMortgagee ??
            (vehicle.HasVehicleMortgagee ? 'yes' : undefined),
    }
}
